import { useState } from 'react';
import { NJButton, NJCheckbox, NJHeading, NJText } from '@engie-group/fluid-design-system-react';
import PrototypeStudioLayout from '@shared/PrototypeStudioLayout';
import { UMFormStyles, UMIcon, UMR } from '../../urbanmap/_components';
import { energyLabel } from './data';
import { useStudio } from './hooks';
import { HypothesesPanel } from './HypothesesPanel';
import { NetworkMap } from './NetworkMap';
import { PlanPanel } from './PlanPanel';
import { ProjectSettings } from './ProjectSettings';
import { RecalculateDialog } from './RecalculateDialog';
import { Wireframes } from './Wireframes';
import type { Hypotheses, MapTool } from './types';
import './styles.css';

export const meta = {
  title: 'Plan · scénarios et plans de développement',
  description: 'Paramétrer les routes et les départs, générer plusieurs plans, phaser et recalculer sans perte.',
  tags: ['editor', 'planning', 'network'],
  author: 'DT5993', status: 'draft',
  updatedAt: '2026-10-20',
};

export default function ScenarioPlansPrototype() {
  const studio = useStudio();
  const { state, mutation } = studio;
  const [scenarioId, setScenarioId] = useState(state.scenarios[0]?.id ?? '');
  const [planId, setPlanId] = useState<string | null>(null);
  const [projectOpen, setProjectOpen] = useState(false);
  const [selectedRoad, setSelectedRoad] = useState('r9');
  const [tool, setTool] = useState<MapTool>('inspect');
  const [confirm, setConfirm] = useState(false);
  const [simulateFailure, setSimulateFailure] = useState(false);
  const scenario = state.scenarios.find(s => s.id === scenarioId) ?? state.scenarios[0];
  const plan = state.plans.find(p => p.id === planId);
  const wireframes = new URLSearchParams(window.location.search).get('view') === 'wireframes';
  const select = (s: string, p: string | null = null) => {
    setScenarioId(s); setPlanId(p); setProjectOpen(false); setTool('inspect'); mutation.reset();
  };
  const run = (hypotheses: Hypotheses) => {
    if (!plan) return;
    mutation.mutate({
      plan: structuredClone(plan), hypotheses: structuredClone(hypotheses),
      networkKind: state.project.networkKind, fail: simulateFailure,
    });
  };
  return <PrototypeStudioLayout title={meta.title}>
    <div className="sp-root" style={{ fontFamily: UMR.font }}>
      <UMFormStyles />
      <header className="sp-header">
        <div className="sp-logo"><UMIcon name="hub" size={26} color="currentColor" /> <strong>PLAN</strong></div>
        <div className="sp-header-title">
          <NJText as="div"><strong>{state.project.name}</strong></NJText>
          <NJText as="div" scale="sm">Projet existant · {state.project.perimeter} · {state.project.networkKind === 'existing' ? 'Réseau existant' : 'Réseau nouveau'}</NJText>
        </div>
        <NJButton label="Revue du parcours" href="?view=wireframes" scale="sm" emphasis="minimal" aria-disabled={mutation.isPending} />
        <NJButton label="Paramètres du projet" icon="settings" scale="sm" emphasis="subtle"
          disabled={mutation.isPending} onClick={() => { setProjectOpen(true); setTool('inspect'); }} />
      </header>
      {studio.storageError && <div className="sp-error" role="alert">
        <NJText>{studio.storageError}</NJText>
        <NJButton label="Réinitialiser la sauvegarde locale" emphasis="subtle" onClick={studio.reset} />
      </div>}
      {wireframes ? <Wireframes /> : <fieldset className="sp-workspace" disabled={mutation.isPending}>
        <legend className="sp-sr-only">Projet, scénarios et plans de développement</legend>
        <aside className="sp-navigation" aria-label="Scénarios et plans">
          <div className="sp-nav-heading">
            <NJText as="div" scale="sm">PROJET / ÉTUDE</NJText>
            <NJHeading as="h2" scale="sm">Scénarios</NJHeading>
            <NJText scale="sm">Un jeu d’hypothèses, plusieurs plans.</NJText>
          </div>
          <NJButton label="Nouveau scénario" icon="add" scale="sm" emphasis="subtle" onClick={() => select(studio.addScenario())} />
          <ul className="sp-list sp-tree">{state.scenarios.map(s => <li key={s.id}>
            <div className={`sp-tree-item ${scenario?.id === s.id && !plan && !projectOpen ? 'is-active' : ''}`}>
              <NJButton label={s.name} icon="tune" scale="sm" emphasis="minimal" onClick={() => select(s.id)} />
              <NJText as="div" scale="sm">{state.plans.filter(p => p.scenarioId === s.id).length} plan(s)</NJText>
            </div>
            <ul className="sp-list sp-plans">{state.plans.filter(p => p.scenarioId === s.id).map(p => <li key={p.id}
              className={`sp-tree-item ${planId === p.id && !projectOpen ? 'is-active' : ''}`}>
              <NJButton label={p.name || 'Plan sans nom'} icon="route" scale="sm" emphasis="minimal" onClick={() => select(s.id, p.id)} />
              <NJText as="div" scale="sm">{energyLabel(p.energy)} · {p.stale ? 'À recalculer' : p.snapshot ? 'Calculé' : 'Brouillon'}</NJText>
            </li>)}</ul>
            <div className="sp-add-plan"><NJButton label="Ajouter un plan" icon="add" scale="sm" emphasis="minimal"
              onClick={() => select(s.id, studio.addPlan(s.id))} /></div>
          </li>)}</ul>
          <div className="sp-nav-footer">
            <NJText scale="sm">Enregistrement local au navigateur. Aucun appel au moteur Docker.</NJText>
            <NJCheckbox label="Simuler un échec de calcul" isChecked={simulateFailure}
              onChange={(_, checked) => setSimulateFailure(!!checked)} />
          </div>
        </aside>
        <main className="sp-main">
          {projectOpen ? <ProjectSettings project={state.project} save={studio.saveProject} /> :
            scenario ? <>
              <div className="sp-breadcrumb">
                <NJText as="div" scale="sm">Étude / {scenario.name} / {plan ? plan.name : 'Hypothèses du scénario'}</NJText>
                <NJText as="div" scale="sm">{plan ? 'Choix et phasage propres à ce plan' : 'Règles de tracé réutilisables'}</NJText>
              </div>
              <div className="sp-content">
                <NetworkMap hypotheses={scenario.hypotheses} plan={plan} selectedRoad={selectedRoad}
                  networkKind={state.project.networkKind} tool={tool} onSelect={setSelectedRoad}
                  onFinish={() => setTool('inspect')}
                  onDeparture={departure => studio.updateHypotheses(scenario.id, {
                    ...scenario.hypotheses, departures: [...scenario.hypotheses.departures, departure],
                  })}
                  onManual={pipe => { if (plan) studio.updatePlan(plan.id, { pipes: [...plan.pipes, pipe] }); }} />
                {plan ? <PlanPanel key={plan.id} plan={plan} hypotheses={scenario.hypotheses}
                  busy={mutation.isPending} error={mutation.error?.message}
                  update={patch => studio.updatePlan(plan.id, patch)} draw={() => setTool('manual')}
                  calculate={() => { mutation.reset(); if (plan.snapshot || plan.pipes.some(p => p.manual)) setConfirm(true); else run(scenario.hypotheses); }} /> :
                  <HypothesesPanel key={scenario.id} scenario={scenario} selectedRoad={selectedRoad} selectRoad={setSelectedRoad}
                    networkKind={state.project.networkKind} setTool={setTool}
                    save={h => studio.updateHypotheses(scenario.id, h)}
                    rename={name => studio.setState(s => ({
                      ...s, scenarios: s.scenarios.map(c => c.id === scenario.id ? { ...c, name } : c),
                    }))} />}
              </div>
            </> : <div className="sp-empty"><NJText>Aucun scénario. Créez un premier jeu d’hypothèses.</NJText></div>}
        </main>
      </fieldset>}
      {mutation.isPending && <div className="sp-running" role="status"><NJText>Calcul du tracé en cours… Les modifications sont temporairement suspendues.</NJText></div>}
      {mutation.isSuccess && !mutation.isPending && <div className="sp-success" role="status"><NJText>Tracé généré. Complétez le phasage des nouvelles canalisations.</NJText></div>}
      {confirm && plan && scenario && <RecalculateDialog plan={plan} current={scenario.hypotheses}
        onClose={() => setConfirm(false)} onConfirm={run} />}
    </div>
  </PrototypeStudioLayout>;
}
